import { useState, useEffect } from "react";
import { useAuth, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { FileText, Shield, DollarSign, Lock, Users, Clock, CheckCircle2 } from "lucide-react";

const sectionIcons = {
  "Revenue Sharing": DollarSign,
  "Payment Terms": Clock,
  "Confidentiality & NDA": Lock,
  "Non-Circumvention": Shield,
  "Platform Usage": Users,
  "Termination": FileText,
};

export default function ContractorAgreement({ onAccepted }) {
  const { user } = useAuth();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const res = await apiClient.get("/contractor-agreement/template");
        setTemplate(res.data);
      } catch {
        toast.error("Failed to load agreement");
      } finally {
        setLoading(false);
      }
    };
    fetchTemplate();
  }, []);

  const handleAccept = async () => {
    if (!checked) return;
    setAccepting(true);
    try {
      await apiClient.post("/contractor-agreement/accept", { accepted: true });
      toast.success("Agreement accepted! Welcome to SAG.");
      onAccepted?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept agreement");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-sag-navy font-heading text-xl">Loading agreement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4" data-testid="contractor-agreement-page">
      <Card className="w-full max-w-3xl shadow-xl border-0">
        {/* Header */}
        <div className="bg-sag-navy text-white p-8 rounded-t-xl">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-8 w-8" />
            <h1 className="font-heading text-2xl font-bold">Independent Contractor Agreement</h1>
          </div>
          <p className="text-white/80 text-sm">
            Welcome, {user?.full_name}. Before accessing the SAG Helpdesk Platform, please review and accept the following agreement.
          </p>
          <div className="mt-4 flex gap-6">
            <div className="bg-white/10 rounded-lg px-4 py-2">
              <p className="text-xs text-white/60">SAG Revenue Share</p>
              <p className="text-2xl font-bold">{template?.revenue_split_sag || 60}%</p>
            </div>
            <div className="bg-white/10 rounded-lg px-4 py-2">
              <p className="text-xs text-white/60">Your Revenue Share</p>
              <p className="text-2xl font-bold">{template?.revenue_split_contractor || 40}%</p>
            </div>
            <div className="bg-white/10 rounded-lg px-4 py-2">
              <p className="text-xs text-white/60">Agreement Version</p>
              <p className="text-2xl font-bold">v{template?.version}</p>
            </div>
          </div>
        </div>

        {/* Agreement Sections */}
        <CardContent className="p-0">
          <ScrollArea className="h-[400px]">
            <div className="p-6 space-y-6">
              {template?.sections?.map((section, idx) => {
                const Icon = sectionIcons[section.heading] || FileText;
                return (
                  <div key={idx}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-2 rounded-lg bg-sag-navy/5">
                        <Icon className="h-4 w-4 text-sag-navy" />
                      </div>
                      <h3 className="font-heading font-bold text-slate-900">{section.heading}</h3>
                    </div>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed pl-10">
                      {section.content}
                    </p>
                    {idx < template.sections.length - 1 && <Separator className="mt-6" />}
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {/* Acceptance Footer */}
          <div className="border-t border-slate-200 p-6 bg-slate-50 rounded-b-xl">
            <div className="flex items-start gap-3 mb-4">
              <input
                type="checkbox"
                id="accept-agreement"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-1 rounded border-slate-300"
                data-testid="agreement-checkbox"
              />
              <label htmlFor="accept-agreement" className="text-sm text-slate-700 cursor-pointer">
                I, <strong>{user?.full_name}</strong>, have read and understood the terms of this Independent Contractor Agreement.
                I agree to the <strong>60/40 revenue split</strong> (60% SAG, 40% Contractor), the confidentiality obligations,
                non-circumvention clause, and all other terms outlined above. I understand this is a legally binding digital acceptance.
              </label>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">
                Your IP address and timestamp will be recorded upon acceptance.
              </p>
              <Button
                onClick={handleAccept}
                disabled={!checked || accepting}
                className="bg-sag-navy hover:bg-sag-navy-800 px-8"
                data-testid="accept-agreement-button"
              >
                {accepting ? "Processing..." : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Accept Agreement
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
